import { Formik } from "formik";
import { useEffect, useState, useMemo } from "react";
import { Form } from "react-bootstrap";
import { Button} from '@blueprint/ui';
import * as yup from "yup";

import { useRoleListByProfileType } from "../../../api/admin";
import { useProfileListByType, useProfileTypeList, UserProfile } from "../../../api/profile";
import { Loader } from "../../../common/components/core";
import {
    FormContainer,
    FormFooter,
    InputField,
    SelectField,
} from "../../../common/components/form";
import { FormSubmissionHandler } from "../../../types/common";
import { isUserAdmin, parseRoleToText } from "../../../utils/role";
import { User } from "../../../types/users";
import { parseUserProfileTypeToText } from "../../../utils/userProfile";
import { ProjectUserRole } from "../../../types/project";
import useUpdateUserHandler from "../hooks/useUpdateUserHandler";
import userCurrentApi from "../../../api/userCurrent";
import { useSelector } from "../../../redux/hooks";
import { currentRolesSelector } from "../../../redux/slices/auth/selectors";
import { SelectOption } from "../../../common/components/form/Select";
import { ProfileType } from "../../EntityManagement/ProfileManageMentView";
import { UserCurrent, UserRole } from "../../../types/userCurrent";
// import { profile } from "console";

export type UpdateUserFormValues = {
    firstName: string;
    lastName: string;
    email: string;
    profileType: string;
    userProfileId: string;
    role: string;
};

const schema: yup.SchemaOf<UpdateUserFormValues> = yup.object().shape({
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    email: yup.string().email().required(),
    profileType: yup.string().required(),
    userProfileId: yup.string().required(),
    role: yup.string().required(),
});

type UpdateUserFormProps = {
    onCancel: () => void;
    onSubmit: FormSubmissionHandler<UpdateUserFormValues>;
    user: User;
};

const UpdateUserForm = ({
    onCancel,
    onSubmit,
    user,
}: UpdateUserFormProps): JSX.Element => {
    const [firstFetch, setFirstFetch] = useState<boolean>(true);
    const [profileType, setProfileType] = useState<string>(user.profileType);
    const { data: profileList, isValidating: isValidatingProfileList } =
        useProfileTypeList();
    const { data: allProfileNameList } = useProfileListByType(profileType);
    const { data: roleList } = useRoleListByProfileType(profileType);
    const currentRoles = useSelector(currentRolesSelector);
    const isAdmin = isUserAdmin(currentRoles);

    const [currentProfile,setcurrentProfile] = useState<UserProfile|null>(null);
    useEffect( ()=>{
        const fetch = async ()=>{
            const res:UserCurrent= await userCurrentApi.getCurrentUser();
            const profile:UserProfile=res.userProfile;
            setcurrentProfile(profile);
        }
        fetch();
    },[isAdmin]);

    const profileOptions = useMemo(() => {
        const profileDisabledList = [ProfileType.HSBC, ProfileType.APPROVER];

        return profileList?.reduce<SelectOption[]>((options, profile) => {
            const option = {
                value: profile,
                label: parseUserProfileTypeToText(profile),
            };

            if (isAdmin && profileDisabledList.includes(profile))
                return options;

            return [...options, option];
        }, []);
    }, [isAdmin, profileList]);
    const profileNameOptions = useMemo(()=>{

        return allProfileNameList?.reduce<SelectOption[]>((options,profile)=>{
            const option = {
                value: profile.id?.toString()||"",
                label: profile.name,
                isDisabled: false,
            }
            if(isAdmin){
                if(currentProfile && currentProfile.id === profile.id) {
                    return [option];
                } else {
                    return [...options];
                }
            }

            return [...options,option];
        },[]);
    },[isAdmin,allProfileNameList,currentProfile]);


    const roleOptions = useMemo(() => {
        const roleDisabledList = [ProjectUserRole.ADMIN];

        return roleList?.reduce<SelectOption[]>((options, role) => {
            const option = {
                value: role,
                label: parseRoleToText(role),
            };

            if (isAdmin && roleDisabledList.includes(role)) return options;

            return [...options, option];
        }, []);
    }, [isAdmin, roleList]);
    const [prevConfirmValues, setPrevConfirmValues] =
        useState<UpdateUserFormValues>({
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            profileType: user.profileType,
            userProfileId:
                allProfileNameList
                    ?.find((el) => el.name === user.profileName)
                    ?.id?.toString() || "",
            role: user.role.toString(),
        });
    const { handler: checkUpdateUserRole } = useUpdateUserHandler();

    useEffect(() => {
        if (firstFetch) {
            if (profileList && allProfileNameList && roleList) {
                setFirstFetch(false);
            }
        }
    }, [firstFetch, profileList, allProfileNameList, roleList]);

    useEffect(() => {
        if (firstFetch && user && allProfileNameList) {
            setPrevConfirmValues({
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                profileType: user.profileType,
                userProfileId:
                    allProfileNameList
                        ?.find((el) => el.name === user.profileName)
                        ?.id?.toString() || "",
                role: user.role.toString(),
            });
        }
    }, [firstFetch, allProfileNameList, user]);

    const updateRoleCancel = (
        setFieldValue: (
            field: string,
            value: any,
            shouldValidate?: boolean | undefined,
        ) => void,
    ) => {
        if (prevConfirmValues) {
            setFieldValue("profileType", prevConfirmValues.profileType);
            setProfileType(prevConfirmValues.profileType);
            setFieldValue("userProfileId", prevConfirmValues.userProfileId);
            setFieldValue("role", prevConfirmValues.role);
        }
    };

    const updateRoleConfirm = (
        newValue: UserRole,
        formValues: UpdateUserFormValues,
        setFieldValue: (
            field: string,
            value: any,
            shouldValidate?: boolean | undefined,
        ) => void,
    ) => {
        if (newValue) {
            setFieldValue("role", newValue);
            const confirmValues: UpdateUserFormValues = {
                ...formValues,
                role: newValue,
            };
            setPrevConfirmValues(confirmValues);
        }
    };

    return (
        <Loader overlay={true} isLoading={isValidatingProfileList}>
            {(firstFetch && profileList && allProfileNameList && roleList) ||
                (!firstFetch && (
                    <Formik
                        validationSchema={schema}
                        validateOnBlur={false}
                        validateOnChange={false}
                        onSubmit={onSubmit}
                        initialValues={{
                            firstName: user.firstName,
                            lastName: user.lastName,
                            email: user.email,
                            profileType: user.profileType,
                            userProfileId:
                                allProfileNameList
                                    ?.find((el) => el.name === user.profileName)
                                    ?.id?.toString() || "",
                            role: user.role.toString(),
                        }}
                    >
                        {({ handleSubmit, setFieldValue, values }) => (
                            <FormContainer>
                                <h1 className="mb-3 rem-1-5">
                                    Edit User Information
                                </h1>
                                <Form
                                    autoComplete="off"
                                    noValidate
                                    onSubmit={handleSubmit}
                                >
                                    <InputField
                                        name="firstName"
                                        label="First Name"
                                        type="text"
                                        maxLength={30}
                                        onBlur={() =>
                                            setFieldValue(
                                                "name",
                                                values.firstName.trim(),
                                            )
                                        }
                                        required={true}
                                    />
                                    <InputField
                                        name="lastName"
                                        label="Last Name"
                                        type="text"
                                        maxLength={30}
                                        onBlur={() =>
                                            setFieldValue(
                                                "name",
                                                values.lastName.trim(),
                                            )
                                        }
                                        required={true}
                                    />
                                    <InputField
                                        name="email"
                                        label="Email address"
                                        type="text"
                                        maxLength={50}
                                        onBlur={() =>
                                            setFieldValue(
                                                "email",
                                                values.email.trim(),
                                            )
                                        }
                                        disabled={true}
                                        required={true}
                                    />
                                    <SelectField
                                        name="profileType"
                                        label="Profile Type"
                                        options={profileOptions || []}
                                        onChange={(data: any) => {
                                            setFieldValue("role", "");
                                            setFieldValue("userProfileId", "");
                                            data && setProfileType(data.value);
                                        }}
                                        disabled={true}
                                    />
                                    <SelectField
                                        name="userProfileId"
                                        label="Profile Name"
                                        options={profileNameOptions || []}
                                        disabled={true}
                                        required={true}
                                    />
                                    <SelectField
                                        name="role"
                                        label="Role Type"
                                        options={roleOptions || []}
                                        disabled={!values.profileType}
                                        onChange={(data: any) => {
                                            data &&
                                                checkUpdateUserRole(
                                                    data.value,
                                                    prevConfirmValues.role,
                                                    () =>
                                                        updateRoleConfirm(
                                                            data.value,
                                                            values,
                                                            setFieldValue,
                                                        ),
                                                    () =>
                                                        updateRoleCancel(
                                                            setFieldValue,
                                                        ),
                                                );
                                        }}
                                        required={true}
                                    />
                                    <FormFooter className="d-flex justify-content-between">
                                        <Button
                                            variant="light.tertiary"
                                            onClick={onCancel}
                                        >
                                            Cancel
                                        </Button>
                                        <Button variant="light.primary" type="submit">
                                            Save
                                        </Button>
                                    </FormFooter>
                                </Form>
                            </FormContainer>
                        )}
                    </Formik>
                ))}
        </Loader>

    );
};

export default UpdateUserForm;
